import type { AuthenticatedAdmin } from "@utopia-well-done-ltd/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type EnvironmentDetail, type EnvironmentPage } from "../api/client.js";
import { can } from "../capabilities.js";
import { ConfirmAction } from "../components/ConfirmAction.js";
import {
  casinoShellPath,
  groundFloorSurfacePath,
  groundFloorWallPath,
  upperFloorSurfacePath,
  upperFloorWallPath,
} from "../environment-floor-geometry.js";
import { environmentFloorPlanPosition } from "../environment-floor-plan.js";
import { asAmount, asMoment } from "../format.js";

const REFRESH_MS = 10_000;

type LifecycleAction = "drain" | "retire";
type TableLifecycleAction = { tableId: string; target: "close" | "open" };

type EnvironmentSummary = EnvironmentPage["environments"][number];

function tableLabel(game: string | null | undefined, placementSlotId: string | null | undefined) {
  const prefix =
    game === "blackjack" ? "BJ" : game === "roulette" ? "R" : game === "poker" ? "P" : "?";
  const suffix = placementSlotId?.match(/load-(\d{2})$/)?.[1] ?? "M";
  return `${prefix} ${suffix}`;
}

function visibleState(environment: EnvironmentSummary) {
  return environment.tables.some(({ quarantined }) => quarantined)
    ? "quarantined"
    : environment.lifecycle;
}

export function Environments({ admin }: { admin: AuthenticatedAdmin }) {
  const [environments, setEnvironments] = useState<EnvironmentSummary[] | null>(null);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [detail, setDetail] = useState<EnvironmentDetail | null>(null);
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [action, setAction] = useState<LifecycleAction | null>(null);
  const [tableAction, setTableAction] = useState<TableLifecycleAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const listRequest = useRef<AbortController | null>(null);
  const detailRequest = useRef<AbortController | null>(null);
  const detailDialog = useRef<HTMLDialogElement>(null);
  const detailOpen = detail !== null;

  const load = useCallback(async (retired: boolean, polling = false, before?: string) => {
    if (!before) listRequest.current?.abort();
    const controller = new AbortController();
    if (!before) listRequest.current = controller;
    setError(null);
    try {
      const page = await api.environments({
        ...(before ? { before } : {}),
        includeRetired: retired,
        polling,
        signal: controller.signal,
      });
      setEnvironments((current) =>
        before ? [...(current ?? []), ...page.environments] : page.environments,
      );
      setNextBefore(page.nextBefore);
    } catch (caught) {
      if (!controller.signal.aborted)
        setError(caught instanceof ApiError ? caught.message : "Could not load environments.");
    } finally {
      if (!before && listRequest.current === controller) setLoading(false);
    }
  }, []);

  const inspect = useCallback(async (environmentInstanceId: string) => {
    detailRequest.current?.abort();
    const controller = new AbortController();
    detailRequest.current = controller;
    setSelectedTableId(null);
    setError(null);
    try {
      setDetail(await api.environment(environmentInstanceId, controller.signal));
    } catch (caught) {
      if (!controller.signal.aborted)
        setError(
          caught instanceof ApiError ? caught.message : "Could not inspect the environment.",
        );
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    setDetail(null);
    void load(includeRetired);
    const interval = window.setInterval(() => void load(includeRetired, true), REFRESH_MS);
    return () => {
      window.clearInterval(interval);
      listRequest.current?.abort();
    };
  }, [includeRetired, load]);

  useEffect(() => () => detailRequest.current?.abort(), []);

  useEffect(() => {
    if (!detailOpen) return;
    if (detailDialog.current?.showModal && !detailDialog.current.open) {
      detailDialog.current.showModal();
    } else {
      detailDialog.current?.setAttribute("open", "");
    }
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [detailOpen]);

  async function prepareAction(nextAction: LifecycleAction) {
    if (!detail) return;
    setPending(true);
    setActionError(null);
    try {
      const current = await api.environment(detail.environmentInstanceId);
      setDetail(current);
      if (nextAction === "retire" && !current.drain.canRetire) {
        setError(
          "This environment is not empty yet. Refresh the safety status after play settles.",
        );
        return;
      }
      setAction(nextAction);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Could not refresh the safety status.",
      );
    } finally {
      setPending(false);
    }
  }

  async function confirm(reason: string) {
    if (!detail || !action) return;
    setPending(true);
    setActionError(null);
    try {
      const changed =
        action === "drain"
          ? await api.drainEnvironment(detail.environmentInstanceId, reason)
          : await api.retireEnvironment(detail.environmentInstanceId, reason);
      setDetail(changed);
      setAction(null);
      await load(includeRetired);
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught.message : "The lifecycle change failed.");
    } finally {
      setPending(false);
    }
  }

  async function confirmTable(reason: string) {
    if (!detail || !tableAction) return;
    setPending(true);
    setActionError(null);
    try {
      const changed =
        tableAction.target === "close"
          ? await api.closeTable(detail.environmentInstanceId, tableAction.tableId, reason)
          : await api.openTable(detail.environmentInstanceId, tableAction.tableId, reason);
      setDetail(changed);
      setTableAction(null);
      await load(includeRetired);
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught.message : "The table change failed.");
    } finally {
      setPending(false);
    }
  }

  if (loading && !environments) return <p className="empty">Loading environments…</p>;

  return (
    <section className="body environment-operations">
      <div className="environment-toolbar">
        <span className="dim label-13">Auto-refreshes every 10 seconds</span>
        <label className="environment-retired-toggle">
          <input
            checked={includeRetired}
            onChange={(event) => setIncludeRetired(event.target.checked)}
            type="checkbox"
          />
          Show retired environments
        </label>
      </div>

      {error ? (
        <div className="banner" role="alert">
          {error}
        </div>
      ) : null}

      <div className="environment-layout">
        <div className="card environment-list">
          <table>
            <thead>
              <tr>
                <th>Environment</th>
                <th>Scene</th>
                <th>Status</th>
                <th>Tables</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {(environments ?? []).map((environment) => {
                const state = visibleState(environment);
                return (
                  <tr
                    aria-selected={
                      detail?.environmentInstanceId === environment.environmentInstanceId
                    }
                    className={`clickable-row${
                      detail?.environmentInstanceId === environment.environmentInstanceId
                        ? " selected"
                        : ""
                    }`}
                    key={environment.environmentInstanceId}
                    onClick={() => void inspect(environment.environmentInstanceId)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        void inspect(environment.environmentInstanceId);
                      }
                    }}
                    tabIndex={0}
                  >
                    <td className="mono-12">{environment.environmentInstanceId}</td>
                    <td>{environment.sceneId}</td>
                    <td>
                      <span className={`environment-state environment-state-${state}`}>
                        {state}
                      </span>
                    </td>
                    <td>{environment.tables.length}</td>
                    <td>{asMoment(environment.updatedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(environments ?? []).length === 0 ? (
            <p className="empty">No environments match this view.</p>
          ) : null}
          {nextBefore ? (
            <div className="pagination">
              <button
                className="btn btn-quiet btn-sm"
                onClick={() => void load(includeRetired, false, nextBefore)}
                type="button"
              >
                Load more
              </button>
            </div>
          ) : null}
        </div>

        {detail ? (
          <dialog
            aria-label="Environment detail"
            className="round-dialog environment-detail-dialog"
            onCancel={() => setDetail(null)}
            ref={detailDialog}
          >
            <div className="round-dialog-head environment-detail-head">
              <div>
                <h2>Environment detail</h2>
                <p className="label-13 dim mono-12">{detail.environmentInstanceId}</p>
              </div>
              <span className={`environment-state environment-state-${visibleState(detail)}`}>
                {visibleState(detail)}
              </span>
              <button
                aria-label="Close environment detail"
                className="btn btn-quiet btn-sm"
                onClick={() => setDetail(null)}
                type="button"
              >
                Close
              </button>
            </div>
            <div className="round-dialog-body environment-detail">
              <dl className="environment-facts">
                <div>
                  <dt>ID</dt>
                  <dd className="mono-12">{detail.environmentInstanceId}</dd>
                </div>
                <div>
                  <dt>Owner</dt>
                  <dd>{detail.ownerProcessId ?? "No owner"}</dd>
                </div>
                <div>
                  <dt>Owner lease</dt>
                  <dd>
                    {detail.ownerLeaseExpiresAt ? asMoment(detail.ownerLeaseExpiresAt) : "None"}
                  </dd>
                </div>
                <div>
                  <dt>Lease / protocol</dt>
                  <dd>
                    {detail.leaseVersion} / {detail.protocolVersion}
                  </dd>
                </div>
                <div>
                  <dt>Scene hash</dt>
                  <dd className="mono-12 environment-hash">{detail.sceneContentHash}</dd>
                </div>
              </dl>

              <div className="environment-safety">
                <h2 className="label-14-strong">Retirement safety</h2>
                <dl className="environment-safety-grid">
                  <div>
                    <dt>Active sessions</dt>
                    <dd>{detail.drain.activeSessions}</dd>
                  </div>
                  <div>
                    <dt>Occupied seats</dt>
                    <dd>{detail.drain.occupiedSeats}</dd>
                  </div>
                  <div>
                    <dt>Live rounds</dt>
                    <dd>{detail.drain.liveRounds.length}</dd>
                  </div>
                  <div>
                    <dt>Held balances</dt>
                    <dd>
                      {detail.drain.heldBalances.length
                        ? detail.drain.heldBalances.map((balance) => asAmount(balance)).join(", ")
                        : "None"}
                    </dd>
                  </div>
                </dl>
                {detail.drain.liveRounds.length ? (
                  <ul className="environment-rounds mono-12">
                    {detail.drain.liveRounds.map((round) => (
                      <li key={round.id}>
                        {round.game} · {round.id}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>

              <div className="environment-floor-plan-section">
                <div className="environment-floor-plan-heading">
                  <h2 className="label-14-strong">Table floor plan</h2>
                  <span className="dim label-12">Select a table to inspect it</span>
                </div>
                <div className="environment-floor-plans">
                  {[0, 1].map((floor) => (
                    <div className={`environment-floor environment-floor-${floor + 1}`} key={floor}>
                      <strong>Floor {floor + 1}</strong>
                      <div className="environment-floor-canvas">
                        <svg
                          aria-hidden="true"
                          className="environment-floor-map"
                          preserveAspectRatio="xMidYMid meet"
                          viewBox="-60 -48 120 88"
                        >
                          <path
                            className="environment-floor-surface"
                            d={floor === 0 ? groundFloorSurfacePath : upperFloorSurfacePath}
                            fillRule="evenodd"
                          />
                          <path
                            className="environment-floor-shell"
                            d={casinoShellPath}
                            fillRule="evenodd"
                          />
                          <path
                            className="environment-floor-walls"
                            d={floor === 0 ? groundFloorWallPath : upperFloorWallPath}
                            fillRule="evenodd"
                          />
                        </svg>
                        <span className="environment-floor-axis">
                          Casino collision map · authored coordinates
                        </span>
                        {detail.tables.map((table, index) => {
                          const position = environmentFloorPlanPosition(table.placementSlotId);
                          if (!position || position.floor !== floor) return null;
                          const state = table.quarantined
                            ? "quarantined"
                            : (table.lifecycle ?? "active");
                          return (
                            <button
                              aria-label={`${tableLabel(table.game, table.placementSlotId)} · ${state}`}
                              className={`environment-map-table environment-map-table-${state}`}
                              key={table.tableId}
                              onClick={() => {
                                setSelectedTableId(table.tableId);
                                document
                                  .getElementById(`environment-table-${index}`)
                                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
                              }}
                              style={{ left: `${position.left}%`, top: `${position.top}%` }}
                              title={`${table.placementSlotId ?? table.tableId} · ${state}`}
                              type="button"
                            >
                              {tableLabel(table.game, table.placementSlotId)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="environment-map-legend label-12">
                  <span className="environment-map-legend-item">
                    <i className="active" /> Active
                  </span>
                  <span className="environment-map-legend-item">
                    <i className="closed" /> Closed
                  </span>
                  <span className="environment-map-legend-item">
                    <i className="quarantined" /> Quarantined
                  </span>
                </div>
              </div>

              <div className="environment-tables">
                <h2 className="label-14-strong">Runtime tables</h2>
                {detail.tables.map((table, index) => (
                  <article
                    className={`environment-table${
                      selectedTableId === table.tableId ? " environment-table-selected" : ""
                    }`}
                    id={`environment-table-${index}`}
                    key={table.tableId}
                  >
                    <div>
                      <strong>{table.game ?? "Legacy"}</strong>
                      <span
                        className={`environment-state environment-state-${
                          table.quarantined ? "quarantined" : (table.lifecycle ?? "active")
                        }`}
                      >
                        {table.quarantined ? "quarantined" : (table.lifecycle ?? "legacy")}
                      </span>
                    </div>
                    <code>{table.tableId}</code>
                    <small>
                      {table.placementSlotId ?? "No placement"} ·{" "}
                      {table.definitionId ?? "Legacy definition"}
                    </small>
                    {can(admin.role, "games.lifecycle.write") &&
                    detail.lifecycle === "active" &&
                    (table.lifecycle === "active" || table.lifecycle === "closed") ? (
                      <button
                        className="btn btn-quiet btn-sm"
                        disabled={pending}
                        onClick={() => {
                          setActionError(null);
                          setTableAction({
                            tableId: table.tableId,
                            target: table.lifecycle === "closed" ? "open" : "close",
                          });
                        }}
                        type="button"
                      >
                        {table.lifecycle === "closed" ? "Open table" : "Close table"}
                      </button>
                    ) : null}
                  </article>
                ))}
              </div>

              {can(admin.role, "games.lifecycle.write") ? (
                <div className="environment-actions">
                  {detail.lifecycle === "active" ? (
                    <button
                      className="btn btn-quiet"
                      disabled={pending}
                      onClick={() => void prepareAction("drain")}
                      type="button"
                    >
                      Drain environment
                    </button>
                  ) : null}
                  {detail.lifecycle === "draining" ? (
                    <button
                      className="btn btn-danger"
                      disabled={pending || !detail.drain.canRetire}
                      onClick={() => void prepareAction("retire")}
                      type="button"
                    >
                      Retire environment
                    </button>
                  ) : null}
                  {detail.lifecycle === "draining" && !detail.drain.canRetire ? (
                    <p className="hint">
                      Retirement unlocks only after every round and held balance clears.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>

            {action ? (
              <ConfirmAction
                confirmLabel={action === "drain" ? "Start draining" : "Retire environment"}
                danger={action === "retire"}
                error={actionError}
                maximumReason={1000}
                onCancel={() => {
                  if (!pending) setAction(null);
                }}
                onConfirm={(reason) => void confirm(reason)}
                pending={pending}
                prompt={
                  action === "drain"
                    ? "New joins will stop while current play finishes safely."
                    : "The empty runtime and all of its table placements will be retired."
                }
                title={action === "drain" ? "Drain this environment?" : "Retire this environment?"}
              />
            ) : null}

            {tableAction ? (
              <ConfirmAction
                confirmLabel={tableAction.target === "close" ? "Close table" : "Open table"}
                error={actionError}
                maximumReason={1000}
                onCancel={() => {
                  if (!pending) setTableAction(null);
                }}
                onConfirm={(reason) => void confirmTable(reason)}
                pending={pending}
                prompt={
                  tableAction.target === "close"
                    ? "New players and new hands will stop. A hand already in progress can finish safely."
                    : "The table will admit players and start new hands again."
                }
                title={tableAction.target === "close" ? "Close this table?" : "Open this table?"}
              />
            ) : null}
          </dialog>
        ) : null}
      </div>
    </section>
  );
}
